import { useEffect, useRef } from "react";

const services = [
  {
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect x="4" y="12" width="10" height="18" fill="currentColor" opacity="0.3"/>
        <rect x="7" y="8" width="4" height="22" fill="currentColor" opacity="0.6"/>
        <rect x="16" y="6" width="12" height="24" fill="currentColor" opacity="0.4"/>
        <rect x="18" y="3" width="4" height="27" fill="currentColor"/>
        <rect x="4" y="28" width="24" height="2" fill="currentColor"/>
      </svg>
    ),
    title: "High-Rise Construction",
    desc: "We design and construct iconic residential and commercial towers with precision engineering and modern materials.",
    color: "red-600",
  },
  {
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <path d="M2 20 Q8 10 16 16 Q24 22 30 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M2 26H30" stroke="currentColor" strokeWidth="2" opacity="0.4"/>
        <rect x="8" y="20" width="16" height="8" rx="1" fill="currentColor" opacity="0.2"/>
        <circle cx="6" cy="26" r="2.5" fill="currentColor"/>
        <circle cx="26" cy="26" r="2.5" fill="currentColor"/>
      </svg>
    ),
    title: "Road & Highway Projects",
    desc: "State-of-the-art road networks, highways, and flyovers built to last decades with zero compromises on safety.",
    color: "red-500",
  },
  {
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <path d="M4 28V16L16 4L28 16V28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <rect x="12" y="18" width="8" height="10" fill="currentColor" opacity="0.5"/>
        <rect x="8" y="14" width="6" height="6" fill="currentColor" opacity="0.3"/>
        <rect x="18" y="14" width="6" height="6" fill="currentColor" opacity="0.3"/>
        <path d="M4 16H28" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/>
      </svg>
    ),
    title: "Residential Complexes",
    desc: "Premium gated communities and housing societies delivered on time, with world-class amenities and green spaces.",
    color: "red-700",
  },
  {
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <rect x="2" y="10" width="28" height="20" rx="2" fill="currentColor" opacity="0.15"/>
        <rect x="6" y="14" width="8" height="12" fill="currentColor" opacity="0.5"/>
        <rect x="18" y="14" width="8" height="12" fill="currentColor" opacity="0.5"/>
        <path d="M2 10L16 2L30 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <rect x="12" y="20" width="8" height="10" fill="currentColor" opacity="0.3"/>
      </svg>
    ),
    title: "Industrial Infrastructure",
    desc: "Factories, warehouses, and industrial parks designed for efficiency, compliance, and long-term operational value.",
    color: "red-600",
  },
  {
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M16 4V16L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="16" cy="16" r="2" fill="currentColor"/>
        <path d="M8 16H4M28 16H24" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
      </svg>
    ),
    title: "Project Management",
    desc: "End-to-end project management from planning and procurement to execution and handover — on schedule, on budget.",
    color: "red-500",
  },
  {
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <path d="M16 2L2 10V22L16 30L30 22V10L16 2Z" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M2 10L16 18L30 10" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
        <path d="M16 18V30" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
        <circle cx="16" cy="16" r="3" fill="currentColor" opacity="0.5"/>
      </svg>
    ),
    title: "Smart Urban Development",
    desc: "IoT-enabled smart city infrastructure, sustainable utilities, and integrated urban planning for the cities of tomorrow.",
    color: "red-700",
  },
];

export default function Services() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add("visible");
      }),
      { threshold: 0.15 }
    );
    if (ref.current) {
      ref.current.querySelectorAll(".animate-on-scroll").forEach(el => observer.observe(el));
    }
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20 animate-on-scroll">
          <div className="inline-flex items-center gap-2 text-red-600 text-xs font-semibold tracking-widest uppercase
            bg-red-50 border border-red-100 px-4 py-2 rounded-full mb-6">
            What We Do
          </div>
          <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(2.2rem,5vw,3.5rem)' }}
            className="text-red-950">
            OUR CORE <span className="text-blue-700">SERVICES</span>
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto text-base" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            From groundbreaking to grand opening, NexEdge Infra delivers every phase of infrastructure development with excellence.
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <div key={s.title}
              className="service-card animate-on-scroll group relative bg-white border border-red-100 rounded-2xl p-8
              hover:border-red-300 hover:shadow-2xl hover:shadow-red-100 transition-all duration-400 cursor-pointer"
              style={{ transitionDelay: `${i * 80}ms` }}>

              {/* Top accent bar - Red to Blue gradient */}
              <div className="absolute top-0 left-8 right-8 h-0.5 bg-gradient-to-r from-red-600 to-blue-600 rounded-full
                transform scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left"/>

              <div className={`service-icon w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-6
                group-hover:bg-gradient-to-br group-hover:from-red-600 group-hover:to-blue-600 group-hover:text-white transition-all duration-300`}>
                {s.icon}
              </div>

              <h3 className="text-red-950 font-bold text-xl mb-3"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>
                {s.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {s.desc}
              </p>

              <div className="mt-6 flex items-center gap-2 text-blue-600 text-sm font-semibold group-hover:gap-4 transition-all duration-300 group-hover:text-red-600">
                Learn More
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add custom CSS for animations */}
      <style jsx>{`
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .animate-on-scroll.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}